/**
 * §7.14 Vaccine Allocation. Compares N candidate vaccination strategies
 * (no-vax / slow / fast / risk-prioritized) by running simulateSeirv
 * against the catalog. Reports deaths averted, doses delivered, and
 * cost-per-DALY under each strategy. Cites Emanuel 2020 NEJM allocation
 * framework (PMID-verified via plugin-economic).
 *
 * Engine plumbing exercised:
 *   - simulateSeirv (first UI use)
 *   - yearsOfLifeLost (plugin-economic)
 *   - classifyCostEffectiveness (WHO-CHOICE)
 */

import { useMemo, useState } from 'react';
import { findPathogen, listPathogens } from '@biomax/plugin-pathogen-database';
import { findRegion, listRegions } from '@biomax/plugin-region-database';
import { simulateSeird, simulateSeirv } from '@biomax/plugin-epidemiology';
import { yearsOfLifeLost, classifyCostEffectiveness } from '@biomax/plugin-economic';
import { Sparkline } from '../sparkline';

interface AllocationStrategy {
  id: string;
  label: string;
  vaccinationRatePerDay: number; // fraction of susceptibles vaccinated per day
  ve: number;
  rationale: string;
}

const STRATEGIES: ReadonlyArray<AllocationStrategy> = [
  {
    id: 'no-vax', label: 'No vaccination',
    vaccinationRatePerDay: 0, ve: 0,
    rationale: 'Counterfactual baseline. Establishes worst-case for averted-deaths benchmark.',
  },
  {
    id: 'slow-rollout', label: 'Slow rollout (1%/day)',
    vaccinationRatePerDay: 0.01, ve: 0.85,
    rationale: 'Vaccine-supply-limited scenario — ~120 days to reach 70% coverage.',
  },
  {
    id: 'fast-rollout', label: 'Fast rollout (3%/day)',
    vaccinationRatePerDay: 0.03, ve: 0.85,
    rationale: 'Mass-vaccination capacity scenario — ~40 days to 70% coverage.',
  },
  {
    id: 'risk-prioritized', label: 'Risk-prioritized (2%/day, VE 0.95 on highest-risk first)',
    vaccinationRatePerDay: 0.02, ve: 0.95,
    rationale: 'Front-loaded to elderly + comorbid. Effective VE proxy increased to reflect targeting.',
  },
];

export function VaccineAllocation() {
  const pathogens = listPathogens();
  const regions = listRegions();

  const [pathogenId, setPathogenId] = useState<string>('sars-cov-2-wildtype');
  const [regionId, setRegionId] = useState<string>('usa');
  const [doseCostUsd, setDoseCostUsd] = useState<number>(15);

  const results = useMemo(() => {
    const pathogen = findPathogen(pathogenId);
    const region = findRegion(regionId);
    if (!pathogen || !region) return null;

    const gamma = 1 / pathogen.infectiousPeriodDays.value;
    const sigma = 1 / pathogen.latentPeriodDays.value;
    const beta = pathogen.R0.value * gamma;
    const ifr = pathogen.ifrByAgeBand[Math.floor(pathogen.ifrByAgeBand.length / 2)]?.ifr ?? 0.005;
    const N = region.population.total;
    const duration = 240;

    const gdpPerCapita = 60_000; // illustrative; v0.4 will pull from a region catalog field

    return STRATEGIES.map(s => {
      const sim = s.vaccinationRatePerDay === 0
        ? simulateSeird({
            N, I0: 100, beta, gamma, sigma, ifr,
            durationDays: duration, dtDays: 1,
          })
        : simulateSeirv({
            N, I0: 100, beta, gamma, sigma, ifr,
            v: s.vaccinationRatePerDay, ve: s.ve,
            durationDays: duration, dtDays: 1,
          });

      const deaths = sim.compartments.D?.at(-1) ?? 0;
      const dosesDelivered = sim.compartments.V?.at(-1) ?? 0;
      // YLL distributed by age structure
      const deathCohorts = region.population.ageStructure.map(b => ({
        ageAtDeath: (b.ageBandLow + b.ageBandHigh) / 2,
        count: deaths * b.fraction,
      }));
      const yll = yearsOfLifeLost(deathCohorts);
      const totalDoseCost = dosesDelivered * doseCostUsd;
      return { strategy: s, sim, deaths, dosesDelivered, yll, totalDoseCost, gdpPerCapita };
    });
  }, [pathogenId, regionId, doseCostUsd]);

  if (!results) return <div>Loading…</div>;

  const baseline = results.find(r => r.strategy.id === 'no-vax')!;

  return (
    <div className="surface">
      <div className="surface__header">
        <span className="surface__sec">§7.14</span>
        <h1 className="surface__title">Vaccine Allocation</h1>
      </div>
      <div className="surface__sub">
        {STRATEGIES.length} candidate strategies × simulateSeirv ·
        deaths averted + cost-per-DALY × WHO-CHOICE classification ·
        Emanuel 2020 NEJM allocation framework
      </div>

      <div className="panel">
        <div className="panel__head"><span className="panel__title">Scenario</span></div>
        <div className="simctl">
          <label className="simctl__field">
            <span className="simctl__label">Pathogen</span>
            <select value={pathogenId} onChange={e => setPathogenId(e.target.value)}>
              {pathogens.map(p => <option key={p.id} value={p.id}>{p.names.common}</option>)}
            </select>
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Region</span>
            <select value={regionId} onChange={e => setRegionId(e.target.value)}>
              {regions
                .slice()
                .sort((a, b) => a.names.english.localeCompare(b.names.english))
                .map(r => <option key={r.id} value={r.id}>{r.names.english}</option>)}
            </select>
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Cost per dose · USD</span>
            <input type="number" value={doseCostUsd}
              onChange={e => setDoseCostUsd(parseFloat(e.target.value) || 0)} />
          </label>
        </div>
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Strategy comparison</span>
          <span className="panel__meta">ranked by deaths-averted descending</span>
        </div>
        <table className="dtable">
          <thead>
            <tr>
              <th>Strategy</th>
              <th className="num">Deaths</th>
              <th className="num">Averted vs baseline</th>
              <th className="num">Doses delivered</th>
              <th className="num">Total dose cost</th>
              <th className="num">$/YLL averted</th>
              <th>WHO-CHOICE</th>
            </tr>
          </thead>
          <tbody>
            {results
              .slice()
              .sort((a, b) => a.deaths - b.deaths)
              .map(r => {
                const averted = baseline.deaths - r.deaths;
                const yllAverted = baseline.yll - r.yll;
                const costPerYll = yllAverted > 0 ? r.totalDoseCost / yllAverted : Infinity;
                const cat = r.strategy.id === 'no-vax'
                  ? 'baseline'
                  : classifyCostEffectiveness(costPerYll, r.gdpPerCapita);
                const catColor = cat === 'highly-cost-effective' ? 'var(--bm-accent)'
                              : cat === 'cost-effective' ? 'var(--bm-signal)'
                              : cat === 'baseline' ? 'var(--bm-text-muted)'
                              : 'var(--bm-danger)';
                return (
                  <tr key={r.strategy.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{r.strategy.label}</div>
                      <div style={{ fontSize: 11, color: 'var(--bm-text-muted)', marginTop: 2 }}>{r.strategy.rationale}</div>
                    </td>
                    <td className="num">{Math.round(r.deaths).toLocaleString()}</td>
                    <td className="num" style={{ color: r.strategy.id !== 'no-vax' ? 'var(--bm-accent)' : undefined }}>
                      {r.strategy.id === 'no-vax' ? '—' : Math.round(averted).toLocaleString()}
                    </td>
                    <td className="num">{(r.dosesDelivered / 1e6).toFixed(1)}M</td>
                    <td className="num">${(r.totalDoseCost / 1e9).toFixed(2)}B</td>
                    <td className="num">{Number.isFinite(costPerYll) ? `$${Math.round(costPerYll).toLocaleString()}` : '—'}</td>
                    <td style={{ color: catColor }}>{cat.replace(/-/g, ' ')}</td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Cumulative deaths · all strategies</span>
        </div>
        {results.map(r => (
          <Sparkline
            key={r.strategy.id}
            values={r.sim.compartments.D ?? []}
            label={r.strategy.label}
            stroke={r.strategy.id === 'no-vax' ? 'var(--bm-danger)'
              : r.strategy.id === 'risk-prioritized' ? 'var(--bm-accent)'
              : 'var(--bm-signal)'}
          />
        ))}
      </div>

      <div className="panel">
        <div className="panel__head"><span className="panel__title">Ethics framework</span></div>
        <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--bm-text)' }}>
          Allocation under scarcity must satisfy <strong>Emanuel et al. 2020 NEJM</strong>
          (PMID 32202722, verified) — fair allocation per maximize-benefit, equal-treatment,
          prioritize-the-worst-off, instrumental-value-to-society principles. PaCC does NOT
          rank-order patients; it provides aggregate scenario comparison to inform a human
          decision logged via § 7.17 Decision Log.
        </div>
      </div>
    </div>
  );
}
